import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import { useTranslation } from '../../context/LanguageContext';
import {
    Smartphone,
    Share,
    PlusSquare,
    MoreVertical,
    Download,
    CheckCircle,
    ArrowRight,
    Apple,
    Chrome,
} from 'lucide-react';

const InstallAppPage = () => {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState('ios');

    const iosSteps = [
        {
            step: 1,
            title: t.install?.iosStep1Title || 'Ouvrez Safari',
            description: t.install?.iosStep1Desc || 'Accédez à cabaza.be dans le navigateur Safari sur votre iPhone ou iPad.',
            icon: Chrome,
            tip: t.install?.iosStep1Tip || 'Important : Cette méthode ne fonctionne qu\'avec Safari, pas avec Chrome ou Firefox.',
        },
        {
            step: 2,
            title: t.install?.iosStep2Title || 'Appuyez sur le bouton Partager',
            description: t.install?.iosStep2Desc || 'En bas de l\'écran (iPhone) ou en haut (iPad), appuyez sur l\'icône de partage (carré avec une flèche vers le haut).',
            icon: Share,
            image: '/images/install/ios-share.png',
        },
        {
            step: 3,
            title: t.install?.iosStep3Title || 'Sélectionnez "Sur l\'écran d\'accueil"',
            description: t.install?.iosStep3Desc || 'Faites défiler les options et appuyez sur "Sur l\'écran d\'accueil" (icône +).',
            icon: PlusSquare,
            image: '/images/install/ios-add-home.png',
        },
        {
            step: 4,
            title: t.install?.iosStep4Title || 'Confirmez l\'ajout',
            description: t.install?.iosStep4Desc || 'Appuyez sur "Ajouter" en haut à droite. L\'icône Cabaza apparaîtra sur votre écran d\'accueil.',
            icon: CheckCircle,
        },
    ];

    const androidSteps = [
        {
            step: 1,
            title: t.install?.androidStep1Title || 'Ouvrez Chrome',
            description: t.install?.androidStep1Desc || 'Accédez à cabaza.be dans le navigateur Chrome sur votre téléphone Android.',
            icon: Chrome,
            tip: t.install?.androidStep1Tip || 'Cette méthode fonctionne aussi avec d\'autres navigateurs comme Samsung Internet.',
        },
        {
            step: 2,
            title: t.install?.androidStep2Title || 'Appuyez sur le menu',
            description: t.install?.androidStep2Desc || 'Appuyez sur les trois points verticaux (⋮) en haut à droite de l\'écran.',
            icon: MoreVertical,
            image: '/images/install/android-menu.png',
        },
        {
            step: 3,
            title: t.install?.androidStep3Title || 'Sélectionnez "Installer l\'application"',
            description: t.install?.androidStep3Desc || 'Dans le menu, appuyez sur "Installer l\'application" ou "Ajouter à l\'écran d\'accueil".',
            icon: Download,
            image: '/images/install/android-install.png',
        },
        {
            step: 4,
            title: t.install?.androidStep4Title || 'Confirmez l\'installation',
            description: t.install?.androidStep4Desc || 'Appuyez sur "Installer" dans la fenêtre de confirmation. L\'icône apparaîtra sur votre écran d\'accueil.',
            icon: CheckCircle,
        },
    ];

    const steps = activeTab === 'ios' ? iosSteps : androidSteps;

    return (
        <div className="min-h-screen bg-gray-50">
            <Header />

            <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                {/* Hero Section */}
                <div className="text-center mb-12">
                    <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-cabaza-purple to-cabaza-pink mb-6">
                        <Smartphone className="w-10 h-10 text-white" />
                    </div>
                    <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                        {t.install?.title || 'Installez l\'application Cabaza'}
                    </h1>
                    <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                        {t.install?.subtitle || 'Ajoutez Cabaza à votre écran d\'accueil pour un accès rapide, comme une vraie application mobile. Pas besoin de passer par l\'App Store !'}
                    </p>
                </div>

                {/* PWA Benefits */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
                    <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                        <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center mb-3">
                            <CheckCircle className="w-5 h-5 text-green-600" />
                        </div>
                        <h3 className="font-semibold text-gray-900 mb-1">
                            {t.install?.benefit1Title || 'Accès instantané'}
                        </h3>
                        <p className="text-sm text-gray-600">
                            {t.install?.benefit1Desc || 'Lancez Cabaza directement depuis votre écran d\'accueil'}
                        </p>
                    </div>
                    <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                        <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center mb-3">
                            <Download className="w-5 h-5 text-blue-600" />
                        </div>
                        <h3 className="font-semibold text-gray-900 mb-1">
                            {t.install?.benefit2Title || 'Pas de téléchargement'}
                        </h3>
                        <p className="text-sm text-gray-600">
                            {t.install?.benefit2Desc || 'Aucune installation depuis l\'App Store requise'}
                        </p>
                    </div>
                    <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                        <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center mb-3">
                            <Smartphone className="w-5 h-5 text-purple-600" />
                        </div>
                        <h3 className="font-semibold text-gray-900 mb-1">
                            {t.install?.benefit3Title || 'Expérience native'}
                        </h3>
                        <p className="text-sm text-gray-600">
                            {t.install?.benefit3Desc || 'Fonctionne comme une vraie application mobile'}
                        </p>
                    </div>
                </div>

                {/* Tab Selector */}
                <div className="flex justify-center mb-8">
                    <div className="inline-flex bg-white rounded-xl p-1 shadow-sm border border-gray-200">
                        <button
                            onClick={() => setActiveTab('ios')}
                            className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all ${
                                activeTab === 'ios'
                                    ? 'bg-gradient-to-r from-cabaza-purple to-cabaza-pink text-white shadow-md'
                                    : 'text-gray-600 hover:text-gray-900'
                            }`}
                        >
                            <Apple className="w-5 h-5" />
                            iPhone / iPad
                        </button>
                        <button
                            onClick={() => setActiveTab('android')}
                            className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all ${
                                activeTab === 'android'
                                    ? 'bg-gradient-to-r from-cabaza-purple to-cabaza-pink text-white shadow-md'
                                    : 'text-gray-600 hover:text-gray-900'
                            }`}
                        >
                            <Chrome className="w-5 h-5" />
                            Android
                        </button>
                    </div>
                </div>

                {/* Steps */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
                        <h2 className="text-xl font-bold text-gray-900">
                            {activeTab === 'ios'
                                ? (t.install?.iosTitle || 'Installation sur iPhone / iPad')
                                : (t.install?.androidTitle || 'Installation sur Android')
                            }
                        </h2>
                        <p className="text-gray-600 mt-1">
                            {t.install?.followSteps || 'Suivez ces étapes simples pour ajouter Cabaza à votre écran d\'accueil'}
                        </p>
                    </div>

                    <div className="divide-y divide-gray-100">
                        {steps.map((step, index) => (
                            <div key={index} className="p-6 hover:bg-gray-50 transition-colors">
                                <div className="flex gap-4">
                                    {/* Step number */}
                                    <div className="flex-shrink-0">
                                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cabaza-purple to-cabaza-pink flex items-center justify-center text-white font-bold">
                                            {step.step}
                                        </div>
                                    </div>

                                    {/* Content */}
                                    <div className="flex-1">
                                        <div className="flex items-start gap-3 mb-2">
                                            <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                                                <step.icon className="w-4 h-4 text-gray-600" />
                                            </div>
                                            <h3 className="text-lg font-semibold text-gray-900 pt-1">
                                                {step.title}
                                            </h3>
                                        </div>
                                        <p className="text-gray-600 ml-11">
                                            {step.description}
                                        </p>

                                        {/* Tip box */}
                                        {step.tip && (
                                            <div className="ml-11 mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                                                <p className="text-sm text-amber-800">
                                                    <strong>Astuce :</strong> {step.tip}
                                                </p>
                                            </div>
                                        )}

                                        {/* Placeholder for future screenshots */}
                                        {step.image && (
                                            <div className="ml-11 mt-4 p-4 bg-gray-100 rounded-xl border-2 border-dashed border-gray-300">
                                                <p className="text-sm text-gray-500 text-center">
                                                    {t.install?.screenshotPlaceholder || 'Capture d\'écran illustrative'}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Success message */}
                    <div className="p-6 bg-gradient-to-r from-green-50 to-emerald-50 border-t border-green-100">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                                <CheckCircle className="w-6 h-6 text-green-600" />
                            </div>
                            <div>
                                <h3 className="font-semibold text-green-900">
                                    {t.install?.successTitle || 'C\'est fait !'}
                                </h3>
                                <p className="text-green-700">
                                    {t.install?.successDesc || 'Vous pouvez maintenant accéder à Cabaza directement depuis votre écran d\'accueil, comme n\'importe quelle autre application.'}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* CTA Section */}
                <div className="mt-12 text-center">
                    <p className="text-gray-600 mb-4">
                        {t.install?.notOnMobile || 'Vous n\'êtes pas sur mobile ? Envoyez-vous le lien par email !'}
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Link
                            to="/"
                            className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-cabaza-purple to-cabaza-pink text-white rounded-xl font-semibold hover:shadow-lg hover:scale-[1.02] transition-all"
                        >
                            {t.install?.goToApp || 'Accéder à Cabaza'}
                            <ArrowRight className="w-5 h-5" />
                        </Link>
                        <Link
                            to="/discover"
                            className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-gray-700 border border-gray-200 rounded-xl font-semibold hover:bg-gray-50 transition-all"
                        >
                            {t.install?.learnMore || 'En savoir plus'}
                        </Link>
                    </div>
                </div>

                {/* FAQ Section */}
                <div className="mt-16">
                    <h2 className="text-2xl font-bold text-gray-900 text-center mb-8">
                        {t.install?.faqTitle || 'Questions fréquentes'}
                    </h2>
                    <div className="space-y-4">
                        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                            <h3 className="font-semibold text-gray-900 mb-2">
                                {t.install?.faq1Question || 'Qu\'est-ce qu\'une PWA ?'}
                            </h3>
                            <p className="text-gray-600">
                                {t.install?.faq1Answer || 'Une PWA (Progressive Web App) est une application web qui peut être installée sur votre appareil et fonctionne comme une application native. Elle se lance depuis votre écran d\'accueil et offre une expérience fluide sans passer par les stores d\'applications.'}
                            </p>
                        </div>
                        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                            <h3 className="font-semibold text-gray-900 mb-2">
                                {t.install?.faq2Question || 'L\'application prend-elle de la place sur mon téléphone ?'}
                            </h3>
                            <p className="text-gray-600">
                                {t.install?.faq2Answer || 'Très peu ! Contrairement aux applications traditionnelles, une PWA n\'occupe que quelques mégaoctets. Elle utilise principalement le cache du navigateur.'}
                            </p>
                        </div>
                        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                            <h3 className="font-semibold text-gray-900 mb-2">
                                {t.install?.faq3Question || 'Puis-je recevoir des notifications ?'}
                            </h3>
                            <p className="text-gray-600">
                                {t.install?.faq3Answer || 'Oui, sur Android les notifications push sont pleinement supportées. Sur iOS, le support des notifications est disponible depuis iOS 16.4.'}
                            </p>
                        </div>
                    </div>
                </div>
            </main>

            <Footer />
        </div>
    );
};

export default InstallAppPage;
